/**
 * Blokk 7 T1.20 — Public underdatabehandler-side
 *
 * Viser aktuell katalog over underdatabehandlere, integrasjonspartnere og
 * eksterne datakilder. Henter data fra /underdatabehandlere.json (uautentisert).
 * Ingen login-krav.
 */
import { useEffect, useState } from 'react';
import { Shield, ExternalLink, MapPin, Info, Rss, FileJson } from 'lucide-react';

interface Subprocessor {
  slug: string;
  name: string;
  purpose: string;
  data_categories: string[];
  data_subjects: string[];
  location_country: string;
  transfer_mechanism: string | null;
  security_measures: string | null;
  applies_to_dpa: string[];
  privacy_policy_url: string | null;
  effective_from: string;
}

interface CatalogResponse {
  source: string;
  last_updated: string;
  dpa_filter: string;
  total: number;
  by_category: {
    sub_processor: Subprocessor[];
    independent_controller: Subprocessor[];
    external_source: Subprocessor[];
    not_applicable: Subprocessor[];
  };
}

const CATEGORY_LABELS: Record<string, { title: string; description: string }> = {
  sub_processor: {
    title: 'Underdatabehandlere',
    description: 'Leverandører som behandler personopplysninger på vegne av Kompetanseforbundet under databehandleravtale (GDPR art. 28).'
  },
  independent_controller: {
    title: 'Selvstendige behandlingsansvarlige (integrasjonspartnere)',
    description: 'Tjenester som opptrer på egne vegne med eget rettsgrunnlag — ikke underdatabehandlere, men integrasjonspartnere.'
  },
  external_source: {
    title: 'Eksterne datakilder',
    description: 'Åpne datakilder plattformen henter data fra. Ingen personopplysninger sendes til disse.'
  },
  not_applicable: {
    title: 'Kunde-eide integrasjoner',
    description: 'LMS-plattformer som kunden selv drifter. Ikke underdatabehandler for Kompetanseforbundet.'
  }
};

const DPA_FILTER_OPTIONS = [
  { value: '', label: 'Alle' },
  { value: 'dpa-1', label: 'DPA-1 (studieforbund)' },
  { value: 'dpa-2', label: 'DPA-2 (medlemsorganisasjon)' },
  { value: 'dpa-3', label: 'DPA-3 (LMS)' }
];

export default function UnderdatabehandlerePage() {
  const [data, setData] = useState<CatalogResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dpaFilter, setDpaFilter] = useState<string>('');

  useEffect(() => {
    setLoading(true);
    fetch(`/underdatabehandlere.json${dpaFilter ? `?dpa=${dpaFilter}` : ''}`)
      .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then(json => { setData(json); setLoading(false); })
      .catch(e => { setError(e.message); setLoading(false); });
  }, [dpaFilter]);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-5xl mx-auto px-4 py-10">
        <div className="flex items-start gap-3 mb-2">
          <div className="p-2 bg-kf-blue-100 rounded-lg">
            <Shield className="h-6 w-6 text-kf-blue-700" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Underdatabehandlere</h1>
            <p className="text-sm text-gray-600 mt-1">
              Referanse-basert oversikt per GDPR art. 28(2). Endringer varsles skriftlig 30 dager før nye leverandører tas i bruk, med rett til å motsette seg.
            </p>
          </div>
        </div>

        {data && (
          <div className="text-xs text-gray-500 mb-6">
            Sist oppdatert: {new Date(data.last_updated).toLocaleString('no-NO', { timeZone: 'Europe/Oslo' })}
          </div>
        )}

        <div className="flex flex-wrap items-center gap-2 mb-6">
          <span className="text-sm text-gray-700">Filtrer:</span>
          {DPA_FILTER_OPTIONS.map(opt => (
            <button
              key={opt.value}
              onClick={() => setDpaFilter(opt.value)}
              className={`px-3 py-1 text-sm rounded-full transition-colors ${
                dpaFilter === opt.value
                  ? 'bg-kf-blue-600 text-white'
                  : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-100'
              }`}
            >
              {opt.label}
            </button>
          ))}
          <div className="ml-auto flex items-center gap-2 text-sm text-gray-600">
            <a href={`/underdatabehandlere.json${dpaFilter ? `?dpa=${dpaFilter}` : ''}`}
               className="inline-flex items-center gap-1 hover:text-kf-blue-700">
              <FileJson className="h-4 w-4" /> JSON
            </a>
            <a href="/underdatabehandlere.rss" className="inline-flex items-center gap-1 hover:text-kf-blue-700">
              <Rss className="h-4 w-4" /> RSS
            </a>
          </div>
        </div>

        {loading && <div className="text-gray-600">Laster…</div>}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-800 rounded-lg p-4">
            Kunne ikke laste katalogen: {error}
          </div>
        )}

        {data && Object.entries(data.by_category).map(([category, items]) => {
          if (!items || items.length === 0) return null;
          const label = CATEGORY_LABELS[category];
          if (!label) return null;
          return (
            <section key={category} className="mb-10">
              <h2 className="text-lg font-semibold text-gray-900 mb-1">{label.title}</h2>
              <p className="text-sm text-gray-600 mb-4">{label.description}</p>
              <div className="space-y-3">
                {items.map(sp => (
                  <div key={sp.slug} className="bg-white border border-gray-200 rounded-lg p-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-900">{sp.name}</h3>
                        <p className="text-sm text-gray-700 mt-1">{sp.purpose}</p>
                      </div>
                      {sp.privacy_policy_url && (
                        <a
                          href={sp.privacy_policy_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-sm text-kf-blue-600 hover:text-kf-blue-800 whitespace-nowrap"
                        >
                          Personvern <ExternalLink className="h-3 w-3" />
                        </a>
                      )}
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3 text-xs text-gray-600">
                      <div className="flex items-center gap-1.5">
                        <MapPin className="h-3.5 w-3.5" />
                        <span>Sted: <b>{sp.location_country}</b>{sp.transfer_mechanism && ` · ${sp.transfer_mechanism}`}</span>
                      </div>
                      {sp.applies_to_dpa && sp.applies_to_dpa.length > 0 && (
                        <div className="flex items-center gap-1.5">
                          <span>Gjelder: {sp.applies_to_dpa.map(t => t.toUpperCase()).join(', ')}</span>
                        </div>
                      )}
                    </div>
                    {sp.security_measures && (
                      <div className="mt-2 text-xs text-gray-600 flex items-start gap-1.5">
                        <Info className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                        <span>{sp.security_measures}</span>
                      </div>
                    )}
                    {sp.data_categories && sp.data_categories.length > 0 && sp.data_categories[0] !== 'ingen_persondata' && sp.data_categories[0] !== 'ingen_kandidat_data' && sp.data_categories[0] !== 'ikke_relevant_kunde_egen_installasjon' && (
                      <div className="mt-2 flex flex-wrap gap-1">
                        {sp.data_categories.map(dc => (
                          <span key={dc} className="text-xs px-2 py-0.5 bg-kf-blue-50 text-kf-blue-700 rounded">{dc}</span>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </section>
          );
        })}

        <footer className="border-t border-gray-200 pt-6 mt-10 text-xs text-gray-500">
          <p>Denne siden er referert fra Kompetanseforbundets databehandleravtaler (DPA-1/DPA-2/DPA-3) i seksjon 8 om underdatabehandlere. Endringer varsles skriftlig via e-post 30 dager før aktivering.</p>
          <p className="mt-2">
            Kilde: <a href={data?.source || '/underdatabehandlere'} className="underline hover:text-kf-blue-700">{data?.source || '/underdatabehandlere'}</a>
          </p>
        </footer>
      </div>
    </div>
  );
}
